import { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Filter,
  FileText,
  CheckCircle,
  Eye,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Download,
  MapPin,
  X,
  Building2,
  Shield,
  DollarSign,
  Sparkles,
} from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, parseISO } from "date-fns";
import { useNavigate } from "react-router-dom";
import claimsService from "../../../api/ClaimsService";
import { toast } from "react-toastify";
import Loader from "../../../components/misc/Loader";
import ClaimDetailsModal from "./components/ClaimDetailsModal";

const EmptyState = ({ icon: Icon, title, description }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    className="flex flex-col items-center justify-center py-16"
  >
    <div className="mb-4 p-4 bg-green-100 rounded-full">
      <Icon className="w-12 h-12 text-green-400" />
    </div>
    <h3 className="mb-2 text-lg font-semibold text-gray-900">{title}</h3>
    <p className="text-center text-gray-500 max-w-md">{description}</p>
  </motion.div>
);

export default function ApprovedClaims() {
  const navigate = useNavigate();
  const [globalFilter, setGlobalFilter] = useState("");
  const [providerFilter, setProviderFilter] = useState("all");
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [claimsData, setClaimsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const pageSize = 10;

  useEffect(() => {
    const fetchClaims = async () => {
      setLoading(true);
      try {
        const response = await claimsService.getApprovedClaims();
        
        // Handle response structure
        if (response.status && response.data) {
          let claimsArray = [];
          const data = response.data;
          
          if (Array.isArray(data)) {
            claimsArray = data;
          } else if (data?.data) {
            if (Array.isArray(data.data)) {
              claimsArray = data.data;
            } else if (data.data?.data && Array.isArray(data.data.data)) {
              claimsArray = data.data.data;
            }
          } else if (data?.status_code === 200 && data?.data) {
            if (Array.isArray(data.data)) {
              claimsArray = data.data;
            } else if (data.data?.data && Array.isArray(data.data.data)) {
              claimsArray = data.data.data;
            }
          }
          
          setClaimsData(claimsArray);
        } else {
          toast.error(response.data?.message || response.message || "Failed to fetch approved claims");
          setClaimsData([]);
        }
      } catch (error) {
        toast.error("An error occurred while getting approved claims");
        setClaimsData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchClaims();
  }, []);

  // Calculate stats
  const stats = useMemo(() => {
    const totalClaims = claimsData.length;
    const totalAmount = claimsData.reduce((sum, claim) => {
      // Assuming there's an amount field, adjust based on actual data structure
      return sum + (claim.amount || 0);
    }, 0);
    const uniqueProviders = new Set(
      claimsData
        .map((c) => c.insurance_provider?.company_name || c.insurance_provider_slug)
        .filter(Boolean)
    ).size;
    const uniquePolicies = new Set(
      claimsData
        .map((c) => c.policy?.policy_name || c.policy_slug)
        .filter(Boolean)
    ).size;

    return {
      totalClaims,
      totalAmount,
      uniqueProviders,
      uniquePolicies,
    };
  }, [claimsData]);

  // Get unique providers for filter
  const uniqueProviders = useMemo(() => {
    const providers = new Map();
    claimsData.forEach((claim) => {
      const provider = claim.insurance_provider;
      if (provider) {
        const identifier = provider.slug || provider.company_name;
        const displayName = provider.company_name || provider.slug;
        if (identifier) {
          providers.set(identifier, displayName);
        }
      }
    });
    return Array.from(providers.entries()).map(([identifier, name]) => ({ slug: identifier, name }));
  }, [claimsData]);

  const filteredClaims = useMemo(() => {
    return claimsData.filter((claim) => {
      const user = claim.user || {};
      const policy = claim.policy || {};
      const provider = claim.insurance_provider || {};
      const userName = `${user.first_name || ""} ${user.last_name || ""}`.trim().toLowerCase();
      const userEmail = user.email?.toLowerCase() || "";
      
      const matchesGlobal =
        globalFilter === "" ||
        claim.slug?.toLowerCase().includes(globalFilter.toLowerCase()) ||
        claim.policy_slug?.toLowerCase().includes(globalFilter.toLowerCase()) ||
        policy.policy_name?.toLowerCase().includes(globalFilter.toLowerCase()) ||
        policy.policy_type?.toLowerCase().includes(globalFilter.toLowerCase()) ||
        provider.company_name?.toLowerCase().includes(globalFilter.toLowerCase()) ||
        claim.incident?.[0]?.location?.toLowerCase().includes(globalFilter.toLowerCase()) ||
        claim.incident?.[0]?.type?.toLowerCase().includes(globalFilter.toLowerCase()) ||
        claim.user_slug?.toLowerCase().includes(globalFilter.toLowerCase()) ||
        userName.includes(globalFilter.toLowerCase()) ||
        userEmail.includes(globalFilter.toLowerCase());

      const providerIdentifier = provider.slug || provider.company_name || claim.insurance_provider_slug;
      const matchesProvider =
        providerFilter === "all" ||
        providerIdentifier === providerFilter;

      const matchesDateRange =
        !startDate ||
        !endDate ||
        (claim.incident?.[0]?.date &&
          parseISO(claim.incident[0].date) >= startDate &&
          parseISO(claim.incident[0].date) <= endDate);

      return matchesGlobal && matchesProvider && matchesDateRange;
    });
  }, [claimsData, globalFilter, providerFilter, startDate, endDate]);

  const totalPages = Math.ceil(filteredClaims.length / pageSize);
  const paginatedClaims = filteredClaims.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  if (loading) {
    return <Loader fullScreen={true} text="Loading approved claims..." />;
  }

  return (
    <div className="p-6 min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
      <div className="mx-auto max-w-full">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="mb-4 lg:mb-0">
              <div className="flex items-center space-x-3 mb-2">
                <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl shadow-lg">
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                    Approved Claims
                  </h1>
                  <p className="text-gray-600 mt-1">
                    Successfully processed and approved insurance claims
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate("/admin/insurance/claims")}
                className="flex items-center px-4 py-2 space-x-2 text-gray-700 bg-white rounded-lg border border-gray-200 transition-all duration-200 hover:shadow-md"
              >
                <FileText className="w-4 h-4" />
                <span>All Claims</span>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center px-4 py-2 space-x-2 bg-white rounded-lg border border-gray-200 transition-all duration-200 hover:shadow-md"
              >
                <Download className="w-4 h-4" />
                <span>Export Report</span>
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-2 lg:grid-cols-4"
        >
          <motion.div
            whileHover={{ scale: 1.02, y: -2 }}
            className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border-2 border-green-200 shadow-lg"
          >
            <div className="flex items-center">
              <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl shadow-md">
                <CheckCircle className="w-7 h-7 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Approved</p>
                <p className="text-3xl font-bold text-green-700">{stats.totalClaims}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02, y: -2 }}
            className="p-6 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl border-2 border-blue-200 shadow-lg"
          >
            <div className="flex items-center">
              <div className="p-3 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl shadow-md">
                <DollarSign className="w-7 h-7 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Amount</p>
                <p className="text-3xl font-bold text-blue-700">₵{stats.totalAmount.toLocaleString()}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02, y: -2 }}
            className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border-2 border-purple-200 shadow-lg"
          >
            <div className="flex items-center">
              <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl shadow-md">
                <Building2 className="w-7 h-7 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Providers</p>
                <p className="text-3xl font-bold text-purple-700">{stats.uniqueProviders}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02, y: -2 }}
            className="p-6 bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl border-2 border-orange-200 shadow-lg"
          >
            <div className="flex items-center">
              <div className="p-3 bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl shadow-md">
                <Shield className="w-7 h-7 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Policies</p>
                <p className="text-3xl font-bold text-orange-700">{stats.uniquePolicies}</p>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Filters Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-6 bg-white rounded-xl border-2 border-green-200 shadow-lg"
        >
          <div className="p-6">
            <div className="flex flex-col mb-4 lg:flex-row lg:items-center lg:justify-between">
              <h3 className="mb-4 text-lg font-semibold text-gray-900 lg:mb-0 flex items-center">
                <Sparkles className="mr-2 w-5 h-5 text-green-600" />
                Search & Filters
              </h3>
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center px-4 py-2 space-x-2 text-gray-700 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200 lg:hidden"
              >
                <Filter className="w-4 h-4" />
                <span>Toggle Filters</span>
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${
                    showFilters ? "rotate-180" : ""
                  }`}
                />
              </motion.button>
            </div>

            <AnimatePresence>
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className={`${showFilters ? "block" : "hidden"} lg:block`}
              >
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 transform -translate-y-1/2" />
                    <input
                      type="text"
                      placeholder="Search approved claims..."
                      value={globalFilter}
                      onChange={(e) => setGlobalFilter(e.target.value)}
                      className="py-2 pr-4 pl-10 w-full rounded-lg border-2 border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>

                  <select
                    value={providerFilter}
                    onChange={(e) => setProviderFilter(e.target.value)}
                    className="px-4 py-2 rounded-lg border-2 border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="all">All Providers</option>
                    {uniqueProviders.map((provider) => (
                      <option key={provider.slug} value={provider.slug}>
                        {provider.name}
                      </option>
                    ))}
                  </select>

                  <div className="col-span-1">
                    <DatePicker
                      selectsRange={true}
                      startDate={startDate}
                      endDate={endDate}
                      onChange={(update) => setDateRange(update)}
                      placeholderText="Select date range"
                      className="px-4 py-2 w-full rounded-lg border-2 border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Claims Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="overflow-hidden bg-white rounded-xl border-2 border-green-200 shadow-lg"
        >
          {filteredClaims.length === 0 ? (
            <div className="py-20">
              <EmptyState
                icon={CheckCircle}
                title="No Approved Claims Found"
                description="There are no approved claims matching your current filters. Try adjusting your search criteria."
              />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-green-50 to-emerald-50 border-b-2 border-green-200">
                    <tr>
                      <th className="px-6 py-4 text-sm font-semibold text-left text-gray-700 whitespace-nowrap">Claim ID</th>
                      <th className="px-6 py-4 text-sm font-semibold text-left text-gray-700 whitespace-nowrap">User</th>
                      <th className="px-6 py-4 text-sm font-semibold text-left text-gray-700 whitespace-nowrap">Provider</th>
                      <th className="px-6 py-4 text-sm font-semibold text-left text-gray-700 whitespace-nowrap">Policy</th>
                      <th className="px-6 py-4 text-sm font-semibold text-left text-gray-700 whitespace-nowrap">Incident Date</th>
                      <th className="px-6 py-4 text-sm font-semibold text-left text-gray-700 whitespace-nowrap">Location</th>
                      <th className="px-6 py-4 text-sm font-semibold text-left text-gray-700 whitespace-nowrap">Type</th>
                      <th className="px-6 py-4 text-sm font-semibold text-left text-gray-700 whitespace-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <AnimatePresence>
                      {paginatedClaims.map((claim, index) => {
                        const incident = claim.incident?.[0] || {};
                        const user = claim.user || {};
                        const policy = claim.policy || {};
                        const provider = claim.insurance_provider || {};
                        const userName = `${user.first_name || ""} ${user.last_name || ""}`.trim() || "N/A";

                        return (
                          <motion.tr
                            key={claim.slug}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="transition-colors hover:bg-green-50/50"
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <motion.div
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                className="inline-flex items-center px-3 py-1.5 rounded-lg bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200"
                              >
                                <div className="flex items-center space-x-2">
                                  <div className="p-1 bg-green-100 rounded">
                                    <FileText className="w-3 h-3 text-green-600" />
                                  </div>
                                  <span className="font-mono text-xs font-semibold text-green-700">
                                    {claim.slug?.slice(0, 8).toUpperCase()}
                                  </span>
                                </div>
                              </motion.div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm">
                                <div className="flex items-center">
                                  <p className="font-semibold text-gray-900 truncate">{userName}</p>
                                </div>
                                <p className="text-xs text-gray-500 truncate">{user.email || "N/A"}</p>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center text-sm">
                                {provider.company_logo && (
                                  <img 
                                    src={provider.company_logo} 
                                    alt={provider.company_name}
                                    className="mr-2 w-6 h-6 rounded object-cover flex-shrink-0"
                                  />
                                )}
                                <Building2 className={`mr-2 w-4 h-4 text-gray-400 flex-shrink-0 ${provider.company_logo ? 'hidden' : ''}`} />
                                <div className="min-w-0">
                                  <p className="font-semibold text-gray-900 truncate" title={provider.company_name}>
                                    {provider.company_name || "N/A"}
                                  </p>
                                  {provider.location && (
                                    <p className="text-xs text-gray-500 truncate">{provider.location}, {provider.region}</p>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm">
                                <p className="font-semibold text-gray-900 truncate" title={policy.policy_name}>
                                  {policy.policy_name || "N/A"}
                                </p>
                                <p className="text-xs text-gray-500 capitalize truncate">
                                  {policy.policy_type?.replace(/_/g, " ") || "N/A"}
                                </p>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap">
                              {incident.date
                                ? format(parseISO(incident.date), "MMM dd, yyyy")
                                : "N/A"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center text-sm text-gray-700">
                                <MapPin className="mr-1 w-4 h-4 text-gray-400 flex-shrink-0" />
                                <span className="truncate">{incident.location || "N/A"}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-800">
                                {incident.type || "N/A"}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => {
                                  setSelectedClaim(claim);
                                  setShowDetailsModal(true);
                                }}
                                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-green-600 bg-green-50 rounded-lg transition-colors hover:bg-green-100 border border-green-200"
                              >
                                <Eye className="mr-1 w-4 h-4" />
                                View
                              </motion.button>
                            </td>
                          </motion.tr>
                        );
                      })}
                    </AnimatePresence>
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-6 py-4 bg-gradient-to-r from-green-50 to-emerald-50 border-t-2 border-green-200">
                  <div className="flex flex-col items-center justify-between lg:flex-row">
                    <div className="flex items-center mb-4 space-x-2 lg:mb-0">
                      <span className="text-sm text-gray-700">
                        Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, filteredClaims.length)} of {filteredClaims.length} claims
                      </span>
                    </div>

                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        className="p-2 rounded border-2 border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-green-50 hover:border-green-300 transition-colors"
                      >
                        <ChevronsLeft className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="p-2 rounded border-2 border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-green-50 hover:border-green-300 transition-colors"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>

                      <span className="px-4 py-2 text-sm text-gray-700 font-medium">
                        Page {currentPage} of {totalPages || 1}
                      </span>

                      <button
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage >= totalPages}
                        className="p-2 rounded border-2 border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-green-50 hover:border-green-300 transition-colors"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage >= totalPages}
                        className="p-2 rounded border-2 border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-green-50 hover:border-green-300 transition-colors"
                      >
                        <ChevronsRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </motion.div>
      </div>

      {/* Claim Details Modal */}
      <ClaimDetailsModal
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedClaim(null);
        }}
        claim={selectedClaim}
      />
    </div>
  );
}
